import { useMemo, useState } from "react";
import {
  HiOutlineClipboardList,
  HiOutlineInformationCircle,
  HiOutlineViewGrid,
} from "react-icons/hi";
import "./CardQueryDescription.css";

/**
 * CardQueryDescription — layout clean em duas lâminas
 * - Lâmina esquerda: formulário + adicionais em lista curta de checkboxes
 * - Lâmina direita: resumo do produto (descrição / tags) em card simples
 * - Cabeçalho mínimo com kicker e título
 */
export default function CardQueryDescription({
  title,
  subtitle = "",
  infos = [],
  description = "",
  additionalOptions = [],
  selectedAdditionalIds = [],
  onChangeAdditionalIds = () => {},
  logoOverride,
  kicker = "Consulta",
  children,
  tags = [],
}) {
  const hasAdds =
    Array.isArray(additionalOptions) && additionalOptions.length > 0;
  const hasDescription = Boolean((description || "").trim());
  const hasTags = Array.isArray(tags) && tags.length > 0;

  const selectedSet = useMemo(
    () => new Set((selectedAdditionalIds || []).map(String)),
    [selectedAdditionalIds]
  );

  return (
    <section className="cqd2">
      <div className="cqd2__grid">
        {/* Form + adicionais */}
        <section className="cqd2-card">
          <div className="cqd2-card__header">
            <HiOutlineClipboardList />
            <span>Consulta: {title}</span>
          </div>
          <div className="cqd2-card__body">
            <div className="cqd2__form">{children}</div>

            {hasAdds && (
              <InlineAddons
                options={additionalOptions}
                selectedSet={selectedSet}
                selectedAdditionalIds={selectedAdditionalIds}
                onChangeAdditionalIds={onChangeAdditionalIds}
              />
            )}
          </div>
        </section>

        {/* Resumo */}
        <SummaryCard
          description={description}
          tags={tags}
          hasDescription={hasDescription}
          hasTags={hasTags}
        />
      </div>
    </section>
  );
}

function InlineAddons({
  options = [],
  selectedSet: externalSelectedSet,
  selectedAdditionalIds = [],
  onChangeAdditionalIds = () => {},
}) {
  const selectedSet = useMemo(() => {
    if (externalSelectedSet && typeof externalSelectedSet.has === "function")
      return externalSelectedSet;
    return new Set((selectedAdditionalIds || []).map(String));
  }, [externalSelectedSet, selectedAdditionalIds]);

  const items = useMemo(
    () =>
      (options || []).map((opt) => ({
        id: opt?.id,
        name: opt?.name || "",
        description: opt?.description || "",
        longDescription: opt?.meta?.longDescription || opt?.description || "",
        price: opt?.price,
        disabled: Boolean(opt?.disabled),
      })),
    [options]
  );

  const toggle = (id) => {
    const idStr = String(id);
    const current = new Set((selectedAdditionalIds || []).map(String));
    if (current.has(idStr)) current.delete(idStr);
    else current.add(idStr);
    onChangeAdditionalIds(Array.from(current));
  };

  const [modal, setModal] = useState({ open: false, title: "", text: "" });

  return (
    <div className="cqd2-addons">
      <div className="cqd2-addons__title">Incremente sua consulta</div>
      <div className="cqd2-addons__list">
        {items.length === 0 && (
          <div className="cqd2__empty">Nenhum adicional disponível.</div>
        )}
        {items.map((opt) => {
          const checked = selectedSet.has(String(opt.id));
          const disabled = Boolean(opt.disabled);
          return (
            <label
              key={opt.id}
              className={`cqd2-addon ${checked ? "is-active" : ""} ${
                disabled ? "is-disabled" : ""
              }`}
              role="checkbox"
              aria-checked={checked}
              aria-disabled={disabled}
              tabIndex={disabled ? -1 : 0}
              onClick={() => !disabled && toggle(opt.id)}
              onKeyDown={(e) => {
                if (disabled) return;
                if (e.key === " " || e.key === "Enter") {
                  e.preventDefault();
                  toggle(opt.id);
                }
              }}
            >
              <div className="cqd2-addon__content">
                <div className="cqd2-addon__title">
                  <span className="cqd2-addon__name">{opt.name}</span>
                  <HiOutlineInformationCircle
                    className="cqd2-addon__info"
                    onClick={(e) => {
                      e.stopPropagation();
                      setModal({
                        open: true,
                        title: opt.name,
                        text:
                          opt.longDescription || "Sem descrição disponível.",
                      });
                    }}
                  />
                </div>
              </div>
            </label>
          );
        })}
      </div>

      <InfoModal
        open={modal.open}
        title={modal.title}
        text={modal.text}
        onClose={() => setModal({ open: false, title: "", text: "" })}
      />
    </div>
  );
}

function SummaryCard({ description, tags, hasDescription, hasTags }) {
  const fallback = [
    "Dados cadastrais",
    "Ocorrências / pendências",
    "Protestos e ações",
    "Score e indicadores",
  ];

  return (
    <section className="cqd2-card">
      <div className="cqd2-card__header">
        <HiOutlineViewGrid />
        <span>Resumo</span>
      </div>
      <div className="cqd2-card__body cqd2-card__body--summary">
        {hasDescription ? (
          <div className="cqd2__desc">
            <div className="cqd2__desc-title">Descrição</div>
            <p>{description.trim()}</p>
          </div>
        ) : (
          <div className="cqd2__empty cqd2__empty--rich">
            <div className="cqd2__desc-title">Sem descrição informada</div>
            <p className="cqd2__empty-text">
              Confira os retornos listados ou ative adicionais para detalhar
              mais.
            </p>
            <div className="cqd2__empty-tags">
              {(hasTags ? tags : fallback).slice(0, 4).map((t, i) => (
                <span key={i} className="cqd2-tag cqd2-tag--mini">
                  <span className="cqd2__dot" />
                  {typeof t === "string" ? t : t?.name || "Retorno"}
                </span>
              ))}
            </div>
          </div>
        )}

        {hasTags && (
          <>
            <div className="cqd2__divider">O que retorna</div>
            <TagCloud tags={tags} max={12} />
          </>
        )}
      </div>
    </section>
  );
}

function InfoModal({ open, title, text, onClose }) {
  if (!open) return null;
  return (
    <div className="cqd2-modal" role="dialog" aria-modal="true">
      <div className="cqd2-modal__backdrop" onClick={onClose} aria-hidden />
      <div className="cqd2-modal__content">
        <div className="cqd2-modal__header">
          <h4>{title}</h4>
          <button onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </div>
        <div className="cqd2-modal__body">
          <p>{text || "Sem descrição disponível."}</p>
        </div>
        <div className="cqd2-modal__footer">
          <button onClick={onClose}>Entendi</button>
        </div>
      </div>
    </div>
  );
}

function TagCloud({ tags = [], max = 12 }) {
  const [expanded, setExpanded] = useState(false);

  const sorted = useMemo(() => {
    const arr = (Array.isArray(tags) ? tags : []).map((t) =>
      typeof t === "string" ? { name: t } : t || {}
    );
    const seen = new Set();
    const unique = [];
    for (const t of arr) {
      const key = String(t.name ?? "")
        .trim()
        .toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      unique.push(t);
    }
    return unique.sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), "pt-BR", {
        sensitivity: "base",
        numeric: true,
      })
    );
  }, [tags]);

  const visible = expanded ? sorted : sorted.slice(0, max);
  const hidden = Math.max(sorted.length - visible.length, 0);

  return (
    <div className="cqd2-tags">
      <ul>
        {visible.map((item, i) => (
          <li key={i}>
            <span className="cqd2-tag">
              <span className="cqd2__dot" />
              {item.name}
            </span>
          </li>
        ))}
      </ul>
      {hidden > 0 && !expanded && (
        <button className="cqd2-tag__more" onClick={() => setExpanded(true)}>
          +{hidden}
        </button>
      )}
      {expanded && sorted.length > max && (
        <button className="cqd2-tag__more" onClick={() => setExpanded(false)}>
          Mostrar menos
        </button>
      )}
    </div>
  );
}
